"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreatedByField = void 0;
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const datasheet_1 = require("../../model/datasheet");
const utils_1 = require("../../model/utils");
const store_1 = require("../../exports/store");
const types_1 = require("../../types");
const member_base_field_1 = require("./member_base_field");
const validate_schema_1 = require("./validate_schema");
const i18n_1 = require("../../exports/i18n");
class CreatedByField extends member_base_field_1.MemberBaseField {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.state = state;
    }
    static defaultProperty() {
        return {
            uuids: [],
            datasheetId: '',
            subscription: false,
        };
    }
    validateProperty() {
        return CreatedByField.propertySchema.validate(this.field.property);
    }
    validateCellValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    validateOpenWriteValue() {
        return (0, validate_schema_1.joiErrorResult)("computed field shouldn't validate cellValue");
    }
    validate(cv) {
        return cv == null;
    }
    static createDefault(fieldMap) {
        return {
            id: datasheet_1.DatasheetActions.getNewFieldId(fieldMap),
            type: types_1.FieldType.CreatedBy,
            name: datasheet_1.DatasheetActions.getDefaultFieldName(fieldMap),
            property: this.defaultProperty(),
        };
    }
    get apiMetaProperty() {
        const userMap = store_1.Selectors.getUserMap(this.state);
        return (0, utils_1.getApiMetaUserProperty)(this.field.property.uuids, userMap);
    }
    get openValueJsonSchema() {
        return {
            type: 'object',
            title: this.field.name,
            properties: {
                id: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_creator_ID),
                },
                name: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_creator_name),
                },
                avatar: {
                    type: 'string',
                    title: (0, i18n_1.t)(i18n_1.Strings.robot_variables_creator_avatar),
                },
            }
        };
    }
    get isComputed() {
        return true;
    }
    get basicValueType() {
        return types_1.BasicValueType.String;
    }
    recordEditable() {
        return false;
    }
    // CreatedBy is read-only field, no need to implement this method
    stdValueToCellValue() {
        return null;
    }
    getCellValue(record) {
        var _a;
        const createdBy = (_a = record.recordMeta) === null || _a === void 0 ? void 0 : _a.createdBy;
        return createdBy == null ? member_base_field_1.OtherTypeUnitId.Alien : createdBy;
    }
    // get all records uuid
    getUuidsByRecordMap(recordMap) {
        const uuids = Object.values(recordMap)
            .reduce((ids, record) => {
            var _a, _b;
            if ((_a = record.recordMeta) === null || _a === void 0 ? void 0 : _a.createdBy) {
                ids.push((_b = record.recordMeta) === null || _b === void 0 ? void 0 : _b.createdBy);
            }
            return ids;
        }, []);
        return [...new Set(uuids)];
    }
    getUnitNames(cellValue) {
        const userMap = store_1.Selectors.getUserMap(this.state);
        if (!userMap) {
            return null;
        }
        return cellValue.map(uuid => {
            if (!userMap[uuid]) {
                return '';
            }
            return userMap[uuid].name;
        });
    }
    eq(cv1, cv2) {
        if (cv1 == null || cv2 == null) {
            return cv1 === cv2;
        }
        return (0, lodash_1.isEqual)(cv1, cv2);
    }
    getUnitIds(cellValue) {
        if (!cellValue) {
            return null;
        }
        return [cellValue].flat();
    }
    getUnits(cellValue) {
        const userMap = store_1.Selectors.getUserMap(this.state);
        if (!userMap) {
            return null;
        }
        return cellValue.reduce((res, uuid) => {
            if (userMap[uuid]) {
                res.push(userMap[uuid]);
            }
            return res;
        }, []);
    }
    validateAddOpenFieldProperty(updateProperty) {
        if (updateProperty === null) {
            return { error: undefined, value: null };
        }
        return this.validateUpdateOpenProperty(updateProperty);
    }
}
exports.CreatedByField = CreatedByField;
CreatedByField.propertySchema = joi_1.default.object({
    uuids: joi_1.default.array().items(joi_1.default.string()).required(),
    datasheetId: (0, validate_schema_1.datasheetIdString)().required(),
    subscription: joi_1.default.boolean(),
}).required();
//# sourceMappingURL=created_by_field.js.map